import { useEffect, useRef } from "react";
import { Modal, View, type ModalProps } from "react-native";

import { surface } from "@/src/theme/tokens";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[role='button']",
  "[role='radio']",
  "[role='switch']",
  "[tabindex]",
].join(",");

function focusableChildren(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => {
      if (element.matches("[disabled], [aria-disabled='true'], [aria-hidden='true'], [tabindex='-1']")) {
        return false;
      }
      const bounds = element.getBoundingClientRect();
      if (bounds.width === 0 || bounds.height === 0) return false;

      // Backdrop Pressables wrap the dialog on several screens. They should
      // close on pointer input, but must not become the first keyboard stop.
      return !element.querySelector(FOCUSABLE_SELECTOR);
    });
}

function isVisible(element: HTMLElement) {
  const bounds = element.getBoundingClientRect();
  return bounds.width > 0
    && bounds.height > 0
    && !element.closest("[aria-hidden='true']");
}

/** Web modal boundary with dialog semantics, focus containment and restoration. */
export function InsetModal({ children, visible, onRequestClose, ...props }: ModalProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const previousFocusDescriptorRef = useRef<{ id: string; ariaLabel: string } | null>(null);
  const wasVisibleRef = useRef(false);
  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;

  // React Native Web can move focus while mounting its Modal portal, before
  // effects run. Capture the trigger on the first visible render instead.
  if (visible && !wasVisibleRef.current && typeof document !== "undefined") {
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    previousFocusDescriptorRef.current = previousFocusRef.current
      ? {
          id: previousFocusRef.current.id,
          ariaLabel: previousFocusRef.current.getAttribute("aria-label") ?? "",
        }
      : null;
  }
  wasVisibleRef.current = !!visible;

  useEffect(() => {
    if (!visible) return;

    const focusTimer = window.setTimeout(() => {
      const container = containerRef.current;
      const first = container ? focusableChildren(container)[0] : null;
      first?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.({} as never);
        return;
      }
      if (event.key !== "Tab") return;

      const container = containerRef.current;
      if (!container) return;
      const focusable = focusableChildren(container);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const active = document.activeElement;
      const activeIndex = focusable.findIndex((element) => element === active);
      const nextIndex = event.shiftKey
        ? activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1
        : activeIndex < 0 || activeIndex === focusable.length - 1 ? 0 : activeIndex + 1;
      event.preventDefault();
      event.stopPropagation();
      focusable[nextIndex]?.focus();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", handleKeyDown, true);
      const previousFocus = previousFocusRef.current;
      const descriptor = previousFocusDescriptorRef.current;
      let attempts = 0;
      const restoreFocus = () => {
        const direct = previousFocus?.isConnected && isVisible(previousFocus)
          ? previousFocus
          : null;
        const replacement = direct ?? Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
          .find((element) => isVisible(element) && (
            (!!descriptor?.id && element.id === descriptor.id)
            || (!!descriptor?.ariaLabel && element.getAttribute("aria-label") === descriptor.ariaLabel)
          ));
        if (replacement) {
          replacement.focus();
          return;
        }
        attempts += 1;
        if (attempts < 8) window.setTimeout(restoreFocus, 50);
      };
      window.setTimeout(restoreFocus, 0);
    };
  }, [visible]);

  return (
    <Modal {...props} visible={visible} onRequestClose={onRequestClose}>
      <View
        ref={(node) => { containerRef.current = node as unknown as HTMLElement | null; }}
        role="dialog"
        accessibilityViewIsModal
        style={{ flex: 1, backgroundColor: surface.overlay }}
      >
        {children}
      </View>
    </Modal>
  );
}
